import { useEffect, useRef } from "react";
import {
  getLastActivityAt,
  IDLE_LOGOUT_MS,
  touchLastActivity,
} from "../common/Funcation";

const THROTTLE_MS = 1000;
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousedown",
  "mousemove",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
  "click",
];

type UseIdleLogoutOptions = {
  enabled: boolean;
  onIdle: () => void;
  idleMs?: number;
};

/**
 * Logs the user out after `idleMs` with no activity.
 * Last activity is persisted so a closed tab / sleep still counts.
 */
export const useIdleLogout = ({
  enabled,
  onIdle,
  idleMs = IDLE_LOGOUT_MS,
}: UseIdleLogoutOptions) => {
  const onIdleRef = useRef(onIdle);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const throttleRef = useRef(0);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const stored = getLastActivityAt();
    lastActivityRef.current = stored || Date.now();
    if (!stored) touchLastActivity();

    const scheduleLogout = () => {
      clearTimer();
      const remaining = idleMs - (Date.now() - lastActivityRef.current);
      timerRef.current = setTimeout(() => {
        if (Date.now() - lastActivityRef.current >= idleMs) {
          onIdleRef.current();
        } else {
          scheduleLogout();
        }
      }, Math.max(remaining, 0));
    };

    const markActivity = () => {
      const now = Date.now();
      if (now - throttleRef.current < THROTTLE_MS) return;
      throttleRef.current = now;
      lastActivityRef.current = now;
      touchLastActivity();
      scheduleLogout();
    };

    const logoutIfIdle = () => {
      if (Date.now() - lastActivityRef.current >= idleMs) {
        onIdleRef.current();
        return true;
      }
      return false;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastActivityRef.current = getLastActivityAt() || lastActivityRef.current;
        if (logoutIfIdle()) return;
        scheduleLogout();
      }
    };

    const onPageShow = () => {
      lastActivityRef.current = getLastActivityAt() || lastActivityRef.current;
      if (logoutIfIdle()) return;
      scheduleLogout();
    };

    if (logoutIfIdle()) {
      return;
    }

    scheduleLogout();

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, markActivity, { passive: true });
    });
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, markActivity);
      });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [enabled, idleMs]);
};
